// src/utils/scenarioCalculations.js
// Calculs globaux pour un scénario : collecte des revenus, calcul NET RESTANT

import { calculateFullIR } from './incomeCalculations';

/**
 * Collecte tous les revenus d'un individu pour une année donnée
 * Inclut :
 * - Revenus directs (salaires, pensions, etc.)
 * - Dividendes des sociétés IS détenues (× % détention)
 * - Quote-part des sociétés IR détenues (× % détention)
 *
 * @param {Object} individual - L'individu avec ses données
 * @param {Array} incomes - Les revenus directs de l'individu
 * @param {Object} scenario - Le scénario complet avec boxes, relations, etc.
 * @param {Object} entitiesFinancials - Map des financials par entityId
 * @param {number} year - L'année fiscale
 * @returns {Object} { directIncomes, companyIncomes, allIncomes, summary }
 */
export function collectAllIncomes(individual, incomes, scenario, entitiesFinancials, year) {
  // 1. Revenus directs
  // Transform incomes with yearly_amounts into flat structure for the requested year
  const directIncomes = incomes
    .map(inc => {
      // If income has yearly_amounts, find the amount for the requested year
      if (inc.yearly_amounts && Array.isArray(inc.yearly_amounts)) {
        const yearlyData = inc.yearly_amounts.find(ya => ya.year === year);
        if (yearlyData) {
          return {
            ...inc,
            year,
            grossAmount: yearlyData.amount,
            gross_amount: yearlyData.amount
          };
        }
        return null; // No data for this year
      }
      // Legacy format: income has direct year property
      if (inc.year === year) {
        return inc;
      }
      return null;
    })
    .filter(inc => inc !== null);

  // 2. Revenus provenant des sociétés
  // NOTE: Dividends from IS companies now come exclusively from Income records (type DIVIDENDES)
  // which are auto-generated when shareholdings are created.
  // The old EntityFinancials path has been removed.
  const companyIncomes = [];

  // Résumé
  const summary = {
    directIncomesTotal: directIncomes.reduce((sum, inc) => sum + (inc.grossAmount || inc.gross_amount || 0), 0),
    pfuDividendsTotal: companyIncomes
      .filter(inc => inc.type === 'DIVIDENDES_PFU')
      .reduce((sum, inc) => sum + inc.grossAmount, 0),
    baremeDividendsTotal: companyIncomes
      .filter(inc => inc.type === 'DIVIDENDES_BAREME')
      .reduce((sum, inc) => sum + inc.grossAmount, 0),
    irCompanyIncomeTotal: companyIncomes
      .filter(inc => inc.type.startsWith('QUOTE_PART'))
      .reduce((sum, inc) => sum + inc.grossAmount, 0),
    pfuTaxTotal: companyIncomes
      .filter(inc => inc.type === 'DIVIDENDES_PFU')
      .reduce((sum, inc) => sum + inc.pfuAmount, 0),
    socialChargesTotal: companyIncomes
      .filter(inc => inc.type !== 'DIVIDENDES_PFU')
      .reduce((sum, inc) => sum + (inc.socialCharges || 0), 0)
  };

  return {
    directIncomes,
    companyIncomes,
    allIncomes: [...directIncomes, ...companyIncomes],
    summary
  };
}

/**
 * Calcule le NET RESTANT pour un individu
 * NET = Brut - IR - PS - PFU
 *
 * @param {Object} individual - L'individu
 * @param {Array} incomes - Revenus directs
 * @param {Object} scenario - Le scénario complet
 * @param {Object} entitiesFinancials - Financials des entités
 * @param {number} year - L'année fiscale
 * @returns {Object} Détail du calcul NET
 */
export function calculateNetIncome(individual, incomes, scenario, entitiesFinancials, year) {
  // Collecter tous les revenus
  const collected = collectAllIncomes(individual, incomes, scenario, entitiesFinancials, year);

  // Calculer l'IR sur les revenus au barème
  // (revenus directs + dividendes barème + quote-parts IR)
  const irResult = calculateFullIR(individual, collected.directIncomes, year);

  // Ajouter les dividendes barème au revenu imposable
  const baremeDividendsTaxable = collected.companyIncomes
    .filter(inc => inc.type === 'DIVIDENDES_BAREME')
    .reduce((sum, inc) => sum + inc.taxableAmount, 0);

  const irCompanyIncome = collected.companyIncomes
    .filter(inc => inc.type.startsWith('QUOTE_PART'))
    .reduce((sum, inc) => sum + inc.taxableAmount, 0);

  // Recalculer l'IR avec tous les revenus au barème
  // Pour l'instant, approximation : on ajoute le surplus * TMI
  const additionalTaxableIncome = baremeDividendsTaxable + irCompanyIncome;
  const additionalIR = additionalTaxableIncome * (irResult.marginalRate || 0);

  // Totaux
  const totalGrossIncome =
    collected.summary.directIncomesTotal +
    collected.summary.pfuDividendsTotal +
    collected.summary.baremeDividendsTotal +
    collected.summary.irCompanyIncomeTotal;

  const totalIR = (irResult.finalTax || 0) + additionalIR;
  const totalPFU = collected.summary.pfuTaxTotal;
  const totalPS = collected.summary.socialChargesTotal;
  const totalTax = totalIR + totalPFU + totalPS;
  const netIncome = totalGrossIncome - totalTax;

  return {
    individual: {
      id: individual.individual_id || individual.id,
      name: `${individual.first_name || ''} ${individual.last_name || ''}`.trim()
    },
    year,
    grossIncome: totalGrossIncome,
    directIncome: collected.summary.directIncomesTotal,
    dividendsIncome: collected.summary.pfuDividendsTotal + collected.summary.baremeDividendsTotal,
    companyShareIncome: collected.summary.irCompanyIncomeTotal,
    incomeTax: totalIR,
    pfuTax: totalPFU,
    socialCharges: totalPS,
    totalTax,
    netIncome,
    taxRate: totalGrossIncome > 0 ? (totalTax / totalGrossIncome * 100).toFixed(1) : 0,
    details: {
      irResult,
      collected
    }
  };
}

/**
 * Calcule le DansMaPoche pour tout le scénario
 * = Somme des NET de tous les individus
 *
 * @param {Object} scenario - Le scénario complet
 * @param {Object} allIncomes - Map des incomes par individualId
 * @param {Object} entitiesFinancials - Map des financials par entityId
 * @param {number} year - L'année fiscale
 * @returns {Object} { total, individuals: [{name, net}] }
 */
export function calculateDansMaPoche(scenario, allIncomes, entitiesFinancials, year) {
  const individuals = scenario.individuals || [];
  const primaryIndividualId = scenario.primaryIndividualId || scenario.primary_individual_id;

  const results = individuals.map(ind => {
    const individualId = ind.individual_id || ind.id;
    const incomes = allIncomes[individualId] || [];

    return calculateNetIncome(ind, incomes, scenario, entitiesFinancials, year);
  });

  // If we have a primary individual, use their results for the main display
  // Otherwise aggregate all individuals
  const primaryResult = primaryIndividualId
    ? results.find(r => r.individual.id === primaryIndividualId)
    : null;

  const total = primaryResult
    ? primaryResult.netIncome
    : results.reduce((sum, r) => sum + r.netIncome, 0);

  const totalGross = primaryResult
    ? primaryResult.grossIncome
    : results.reduce((sum, r) => sum + r.grossIncome, 0);

  const totalTax = primaryResult
    ? primaryResult.totalTax
    : results.reduce((sum, r) => sum + r.totalTax, 0);

  // Detailed breakdown for primary individual or aggregate
  const details = primaryResult ? {
    remuneration: primaryResult.directIncome || 0,
    dividendes: primaryResult.dividendsIncome || 0,
    salaires: 0, // TODO: separate salaries from other direct income
    autres: primaryResult.companyShareIncome || 0,
    totalRevenus: primaryResult.grossIncome || 0,
    ir: primaryResult.incomeTax || 0,
    pfu: primaryResult.pfuTax || 0,
    ps: primaryResult.socialCharges || 0,
    totalImpots: primaryResult.totalTax || 0,
    tauxEffectif: primaryResult.taxRate || '0'
  } : {
    remuneration: results.reduce((sum, r) => sum + (r.directIncome || 0), 0),
    dividendes: results.reduce((sum, r) => sum + (r.dividendsIncome || 0), 0),
    salaires: 0,
    autres: results.reduce((sum, r) => sum + (r.companyShareIncome || 0), 0),
    totalRevenus: totalGross,
    ir: results.reduce((sum, r) => sum + (r.incomeTax || 0), 0),
    pfu: results.reduce((sum, r) => sum + (r.pfuTax || 0), 0),
    ps: results.reduce((sum, r) => sum + (r.socialCharges || 0), 0),
    totalImpots: totalTax,
    tauxEffectif: totalGross > 0 ? (totalTax / totalGross * 100).toFixed(1) : '0'
  };

  return {
    year,
    total,
    totalGross,
    totalTax,
    avgTaxRate: results.length > 0
      ? (results.reduce((sum, r) => sum + parseFloat(r.taxRate), 0) / results.length).toFixed(1)
      : 0,
    details,
    individuals: results.map(r => ({
      id: r.individual.id,
      name: r.individual.name,
      gross: r.grossIncome,
      tax: r.totalTax,
      net: r.netIncome,
      taxRate: r.taxRate
    }))
  };
}

/**
 * Vérifie si une date est dans la période donnée
 * @param {Date|string} date - La date à vérifier
 * @param {Date|string} startDate - Début de la période
 * @param {Date|string} endDate - Fin de la période
 * @returns {boolean}
 */
function isDateInPeriod(date, startDate, endDate) {
  if (!date) return false;
  const d = new Date(date);
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

/**
 * Génère les dates de paiement pour un revenu récurrent
 * Gère les revenus avec yearly_amounts (multi-années) et les revenus flat (une année)
 *
 * @param {Object} income - Le revenu avec frequency, paymentDay, yearly_amounts ou year/grossAmount
 * @param {Date} startDate - Début de la période
 * @param {Date} endDate - Fin de la période
 * @returns {Array} Liste des paiements {date, amount, year}
 */
function generatePaymentDates(income, startDate, endDate) {
  const payments = [];
  const frequency = income.frequency || 'ANNUAL';
  const paymentDay = income.paymentDay || income.payment_day || 1;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Get specific payment dates for dividend incomes
  const incomeType = income.income_type || income.incomeType || '';
  const specificFields = income.specific_fields || income.specificFields || {};
  const paymentDates = specificFields.paymentDates || {};

  // Get yearly amounts - either from yearly_amounts array or from direct properties
  const yearlyAmounts = [];

  if (income.yearly_amounts && Array.isArray(income.yearly_amounts)) {
    // Multi-year income with yearly_amounts array
    income.yearly_amounts.forEach(ya => {
      if (ya.year >= startYear && ya.year <= endYear) {
        yearlyAmounts.push({
          year: ya.year,
          amount: ya.amount || 0
        });
      }
    });
  } else if (income.year !== undefined) {
    // Single year income (legacy format)
    const year = income.year;
    if (year >= startYear && year <= endYear) {
      yearlyAmounts.push({
        year: year,
        amount: income.grossAmount || income.gross_amount || 0
      });
    }
  } else {
    // Income without specific year - generate for all years in period
    for (let y = startYear; y <= endYear; y++) {
      yearlyAmounts.push({
        year: y,
        amount: income.grossAmount || income.gross_amount || 0
      });
    }
  }

  // Generate payments for each year
  yearlyAmounts.forEach(({ year, amount }) => {
    if (amount <= 0) return;

    if (frequency === 'ANNUAL') {
      // Annual payment - use specific payment date for dividends if available
      let paymentDate;
      if (incomeType === 'DIVIDENDES' && paymentDates[year]) {
        // Use the actual payment date from exercice data
        paymentDate = new Date(paymentDates[year]);
      } else {
        // Default: Dec 31
        paymentDate = new Date(year, 11, 31); // Month is 0-indexed
      }
      if (isDateInPeriod(paymentDate, start, end)) {
        payments.push({ date: paymentDate, amount, year });
      }
    } else if (frequency === 'MONTHLY') {
      // 12 monthly payments - amount is per month
      for (let month = 0; month < 12; month++) {
        const paymentDate = new Date(year, month, Math.min(paymentDay, 28));
        if (isDateInPeriod(paymentDate, start, end)) {
          payments.push({ date: paymentDate, amount, year });
        }
      }
    } else if (frequency === 'QUARTERLY') {
      // 4 quarterly payments (end of quarter)
      [2, 5, 8, 11].forEach(month => {
        const paymentDate = new Date(year, month, Math.min(paymentDay, 28));
        if (isDateInPeriod(paymentDate, start, end)) {
          payments.push({ date: paymentDate, amount, year });
        }
      });
    }
  });

  return payments;
}

/**
 * Calcule le DansMaPoche pour une période définie par dates
 * Prend en compte :
 * - Capital de départ
 * - Revenus avec leur fréquence et dates de paiement
 * - Dividendes avec leur date de versement effective
 * - Impôts (simplification : déduits proportionnellement)
 *
 * IMPORTANT: Si un primaryIndividualId est défini, seul le cash flow
 * de cette personne est calculé (c'est la personne avec l'étoile)
 *
 * @param {Object} scenario - Le scénario complet avec startDate, endDate, startingCapital
 * @param {Object} allIncomes - Map des incomes par individualId
 * @param {Object} entitiesFinancials - Map des financials par entityId
 * @returns {Object} { total, startingCapital, cashIn, cashOut, details, individuals }
 */
export function calculateDansMaPochePeriod(scenario, allIncomes, entitiesFinancials) {
  const startDate = scenario.startDate ? new Date(scenario.startDate) : null;
  const endDate = scenario.endDate ? new Date(scenario.endDate) : null;
  const startingCapital = scenario.startingCapital || 0;
  const primaryIndividualId = scenario.primaryIndividualId || scenario.primary_individual_id;

  // Si pas de dates définies, pas de calcul possible
  if (!startDate || !endDate) {
    return {
      total: null,
      startingCapital,
      periodStart: null,
      periodEnd: null,
      missingDates: true,
      message: "Veuillez définir les dates de début et de fin du scénario"
    };
  }

  const individuals = scenario.individuals || [];
  const individualResults = [];

  // Track detailed breakdown for the expanded view
  let detailBreakdown = {
    remuneration: 0,
    dividendes: 0,
    salaires: 0,
    autres: 0,
    totalRevenus: 0,
    ir: 0,
    pfu: 0,
    ps: 0,
    totalImpots: 0,
  };

  individuals.forEach(ind => {
    const individualId = ind.individual_id || ind.id;
    const incomes = allIncomes[individualId] || [];

    let indCashIn = 0;
    let indCashOut = 0;
    let indRemuneration = 0;
    let indSalaires = 0;
    let indDividendes = 0;
    let indAutres = 0;
    let indPfu = 0;
    let indPs = 0;
    let indIr = 0;

    // Get all shareholdings for this individual for date validation
    const individualShareholdings = (scenario.shareholdings || []).filter(
      sh => sh.individual_owner_id === individualId || sh.individual_scenario_owner_id === individualId
    );

    // 1. Revenus directs avec dates de paiement
    incomes.forEach(income => {
      const incomeType = income.income_type || income.incomeType || '';
      const payments = generatePaymentDates(income, startDate, endDate);

      payments.forEach(payment => {
        // For dividend income, validate shareholding dates
        if (incomeType === 'DIVIDENDES') {
          const shareholdingId = income.shareholding_id || income.shareholdingId;
          if (shareholdingId) {
            const sh = individualShareholdings.find(s => s.id === shareholdingId);
            if (sh) {
              const shEntryDate = sh.entry_date || sh.entryDate;
              const shExitDate = sh.exit_date || sh.exitDate;

              // Skip if not yet shareholder at payment date
              if (shEntryDate && payment.date < new Date(shEntryDate)) {
                return; // Skip this payment
              }

              // Skip if no longer shareholder at payment date
              if (shExitDate && payment.date > new Date(shExitDate)) {
                return; // Skip this payment
              }
            }
          }
        }

        indCashIn += payment.amount;

        // Categorize income for breakdown
        if (incomeType === 'REMUNERATION_DIRIGEANT') {
          indRemuneration += payment.amount;
        } else if (incomeType === 'SALAIRE') {
          indSalaires += payment.amount;
        } else if (incomeType === 'DIVIDENDES') {
          // Direct dividend income - apply tax based on tax option
          indDividendes += payment.amount;

          // Get tax option from specific_fields or default to PFU
          const specificFields = income.specific_fields || income.specificFields || {};
          const taxOption = specificFields.dividendTaxOption || 'PFU';

          if (taxOption === 'PFU') {
            // PFU: 30% flat tax (12.8% IR + 17.2% PS)
            const pfuAmount = payment.amount * 0.30;
            indCashOut += pfuAmount;
            indPfu += pfuAmount;
          } else {
            // Barème: PS 17.2% + estimated IR on 60% (after 40% abatement)
            const psAmount = payment.amount * 0.172;
            indCashOut += psAmount;
            indPs += psAmount;
            // Estimated IR on taxable amount (60% after abatement)
            const taxableAmount = payment.amount * 0.60;
            const irEstimate = taxableAmount * 0.30; // ~30% TMI estimate
            indIr += irEstimate;
            indCashOut += irEstimate;
          }
        } else {
          indAutres += payment.amount;
        }
      });
    });

    // NOTE: Dividends now come exclusively from Income records (type DIVIDENDES)
    // which are auto-generated when shareholdings are created.
    // The old EntityFinancials path has been removed.

    // Estimate IR on salary/remuneration (simplified)
    const taxableIncome = indSalaires + indRemuneration;
    if (taxableIncome > 0) {
      // Apply 10% deduction then estimate IR
      const afterDeduction = taxableIncome * 0.90;
      // Simple progressive estimate: assume ~20% average rate on taxable income
      const estimatedIr = Math.max(0, (afterDeduction - 11750) * 0.20);
      indIr += estimatedIr;
      indCashOut += estimatedIr;
    }

    individualResults.push({
      id: individualId,
      name: `${ind.first_name || ''} ${ind.last_name || ''}`.trim(),
      cashIn: indCashIn,
      cashOut: indCashOut,
      net: indCashIn - indCashOut,
      breakdown: {
        remuneration: indRemuneration,
        salaires: indSalaires,
        dividendes: indDividendes,
        autres: indAutres,
        pfu: indPfu,
        ps: indPs,
        ir: indIr
      }
    });
  });

  // If we have a primary individual, use their results
  // Otherwise aggregate all individuals
  let primaryResult = null;
  if (primaryIndividualId) {
    primaryResult = individualResults.find(r => r.id === primaryIndividualId);
  }

  let totalCashIn, totalCashOut;

  if (primaryResult) {
    totalCashIn = primaryResult.cashIn;
    totalCashOut = primaryResult.cashOut;
    detailBreakdown = {
      remuneration: primaryResult.breakdown.remuneration,
      salaires: primaryResult.breakdown.salaires,
      dividendes: primaryResult.breakdown.dividendes,
      autres: primaryResult.breakdown.autres,
      totalRevenus: primaryResult.cashIn,
      ir: primaryResult.breakdown.ir,
      pfu: primaryResult.breakdown.pfu,
      ps: primaryResult.breakdown.ps,
      totalImpots: primaryResult.cashOut,
    };
  } else {
    // Aggregate all individuals
    totalCashIn = individualResults.reduce((sum, r) => sum + r.cashIn, 0);
    totalCashOut = individualResults.reduce((sum, r) => sum + r.cashOut, 0);
    detailBreakdown = {
      remuneration: individualResults.reduce((sum, r) => sum + r.breakdown.remuneration, 0),
      salaires: individualResults.reduce((sum, r) => sum + r.breakdown.salaires, 0),
      dividendes: individualResults.reduce((sum, r) => sum + r.breakdown.dividendes, 0),
      autres: individualResults.reduce((sum, r) => sum + r.breakdown.autres, 0),
      totalRevenus: totalCashIn,
      ir: individualResults.reduce((sum, r) => sum + r.breakdown.ir, 0),
      pfu: individualResults.reduce((sum, r) => sum + r.breakdown.pfu, 0),
      ps: individualResults.reduce((sum, r) => sum + r.breakdown.ps, 0),
      totalImpots: totalCashOut,
    };
  }

  // Calculate effective tax rate
  detailBreakdown.tauxEffectif = detailBreakdown.totalRevenus > 0
    ? ((detailBreakdown.totalImpots / detailBreakdown.totalRevenus) * 100).toFixed(1)
    : '0';

  const netCashFlow = totalCashIn - totalCashOut;
  const total = startingCapital + netCashFlow;

  return {
    periodStart: startDate,
    periodEnd: endDate,
    startingCapital,
    cashIn: totalCashIn,
    cashOut: totalCashOut,
    netCashFlow,
    total,
    details: detailBreakdown,
    individuals: individualResults
  };
}
